/* eslint-disable no-duplicate-imports */
import { reactive } from 'vue'
import isEqual from 'lodash/isEqual'

import {
  SEARCH_FILTER_KEYS,
  SEARCH_FILTER_DEFAULTS,
  type SearchAcreageFilterValue,
  type SEARCH_FILTER_PROPERTIES,
} from '@/services/search-filter-service-constants'

type SearchFilterServiceState = {
  isClosed: boolean
  [SEARCH_FILTER_KEYS.ACREAGE]: SearchAcreageFilterValue
}

const observableState = reactive<SearchFilterServiceState>({
  isClosed: true,
  [SEARCH_FILTER_KEYS.ACREAGE]: {
    ...SEARCH_FILTER_DEFAULTS[SEARCH_FILTER_KEYS.ACREAGE],
  },
})

/**
 * Get active status of search filters
 */
export const searchFilterClosed = (): boolean => observableState.isClosed

/**
 * Set active status of search filters
 */
export const setSearchFilterClosed = (value: boolean) =>
  (observableState.isClosed = value)

/**
 * Get filter defaults by key
 */
export const getFilterDefaults = (key: SEARCH_FILTER_KEYS) =>
  SEARCH_FILTER_DEFAULTS[key]

/**
 * Get current acreage filter data
 */
export const acreageFilter = () => {
  return observableState[SEARCH_FILTER_KEYS.ACREAGE]
}

/**
 * Get current filter data by key
 */
export const getFilter = (key: SEARCH_FILTER_KEYS) => observableState[key]

/**
 * Set an entire filters value
 */
export const setFilter = (
  key: SEARCH_FILTER_KEYS,
  value: SearchAcreageFilterValue
) => {
  observableState[key] = value
}

/**
 * Update a specific property in a state value
 */
export const setFilterKey = (
  key: SEARCH_FILTER_KEYS,
  property: SEARCH_FILTER_PROPERTIES,
  value: number
) => {
  observableState[key] = {
    ...observableState[key],
    [property]: value,
  }
}

/**
 * Determine how many filters are currently applied
 */
export const filterCount = (): number => {
  return Object.values(SEARCH_FILTER_KEYS)
    .map((key) => isEqual(getFilter(key), SEARCH_FILTER_DEFAULTS[key]))
    .filter((item) => !item).length
}

/**
 * Determine if a min/max filter has been used
 */
export const hasMinMaxFilter = (key: SEARCH_FILTER_KEYS): boolean => {
  const defaults = getFilterDefaults(key)
  const filter = observableState[key]
  const hasMin = filter.min !== defaults.min
  const hasMax = filter.max !== defaults.max
  return hasMin || hasMax
}

/**
 * Determine if a specific filter property is in use
 */
export const hasFilterProperty = (
  key: SEARCH_FILTER_KEYS,
  property: SEARCH_FILTER_PROPERTIES
): boolean => {
  const defaults = getFilterDefaults(key)
  const filter = observableState[key]
  return filter[property] !== defaults[property]
}
